import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import type {ValueOrderDocument} from "@reva/shared/value-order";

import {buildApiQueryError, type QueryError} from "../util/errorUtil";
import type {ApiQueryError} from "./types";

interface DocumentsState {
    initialized: boolean;
    loading: boolean;
    documents: ValueOrderDocument[] | null;
    queryError: ApiQueryError | null;
}

/** Payload of {@link setQueryError} — a query error plus an optional flag to keep the current documents. */
type DocumentsQueryErrorPayload = QueryError & {keepDocuments?: boolean};

const initialState: DocumentsState = {
    initialized: false,
    loading: false,
    documents: null,
    queryError: null,
};

export const documentsSlice = createSlice({
    name: "documents",
    initialState,
    reducers: {
        resetDocuments: (state) => {
            state.initialized = false;
            state.loading = false;
            state.documents = null;
            state.queryError = null;
        },
        setDocuments: (state, action: PayloadAction<ValueOrderDocument[]>) => {
            state.initialized = true;
            state.loading = false;
            state.documents = action.payload;
            state.queryError = null;
        },
        setLoading: (state) => {
            state.loading = true;
            state.documents = null;
            state.queryError = null;
        },
        setQueryError: (state, action: PayloadAction<DocumentsQueryErrorPayload>) => {
            const {keepDocuments} = action.payload;
            state.initialized = true;
            state.loading = false;
            if (keepDocuments == null) state.documents = null;
            state.queryError = buildApiQueryError(action.payload);
        },
    },
    selectors: {
        selectInitialized: (state) => state.initialized,
        selectLoading: (state) => state.loading,
        selectDocuments: (state) => state.documents,
        selectQueryError: (state) => state.queryError,
        selectSingleDocument: (state, document_type: string) => {
            if (!state.documents) return null;
            return state.documents.find((document) => document.documentType === document_type);
        },
        selectDocumentsByType: (state, document_type: string): ValueOrderDocument[] => {
            if (!state.documents) return [];
            return state.documents.filter((document) => document.documentType === document_type);
        },
    },
});

export const {resetDocuments, setDocuments, setLoading, setQueryError} = documentsSlice.actions;

export const {
    selectInitialized,
    selectLoading,
    selectSingleDocument,
    selectDocumentsByType,
    selectDocuments,
    selectQueryError,
} = documentsSlice.selectors;

export default documentsSlice.reducer;
